import { DomainError, ErrorCode, err, ok, type AppResult } from "@gladis/shared";

/**
 * The energy label, and the ONE place its two spellings meet.
 *
 * The Postgres enum's values are `A_plus_plus`, `A_plus`, `A` … `G`, because an enum label cannot
 * contain `+`. The household reads "A++". Both spellings are real and neither is wrong; what would
 * be wrong is translating between them in two places, which is how the storage form eventually
 * leaks onto a screen or the display form into an insert.
 *
 * So the mapping lives here, both directions plus the option list a form needs — the same argument
 * `formatEur` makes for being the one place an amount becomes a string.
 */

export const ENERGY_LABELS = ["A++", "A+", "A", "B", "C", "D", "E", "F", "G"] as const;

export type EnergyLabelValue = (typeof ENERGY_LABELS)[number];

/** Storage form → display form. The inverse is derived from it, so the two cannot disagree. */
const STORED_TO_DISPLAY = {
  A_plus_plus: "A++",
  A_plus: "A+",
  A: "A",
  B: "B",
  C: "C",
  D: "D",
  E: "E",
  F: "F",
  G: "G",
} as const satisfies Record<string, EnergyLabelValue>;

type StoredEnergyLabel = keyof typeof STORED_TO_DISPLAY;

const DISPLAY_TO_STORED = Object.fromEntries(
  Object.entries(STORED_TO_DISPLAY).map(([stored, display]) => [display, stored]),
) as Record<EnergyLabelValue, StoredEnergyLabel>;

export const EnergyLabel = {
  /** From the wire or a form, in display form. */
  parse(input: string): AppResult<EnergyLabelValue> {
    if ((ENERGY_LABELS as readonly string[]).includes(input)) {
      return ok(input as EnergyLabelValue);
    }
    return err(
      new DomainError(
        ErrorCode.SYS_VALIDATION,
        `An energy label must be one of ${ENERGY_LABELS.join(", ")}.`,
        422,
        { value: input },
      ),
    );
  },

  /** From the column. Validated when it was written, and by the enum itself. */
  fromStored(value: string): EnergyLabelValue {
    return STORED_TO_DISPLAY[value as StoredEnergyLabel];
  },

  /** To the column. The last point at which the storage form exists. */
  toStored(value: EnergyLabelValue): string {
    return DISPLAY_TO_STORED[value];
  },

  /** For a form's select. Ordered best to worst, which is how a label is read. */
  options(): readonly EnergyLabelValue[] {
    return ENERGY_LABELS;
  },
};
